import { alianzaLogos } from '#app/routes/_marketing+/logos/logos.ts'
import { cn } from '#app/utils/misc.tsx'

interface CarruselLogosAlianzasProps {
	className?: string
	autoPlay?: boolean
	speed?: 'slow' | 'normal' | 'fast'
}

export function CarruselLogosAlianzas({
	className,
	autoPlay = true,
	speed = 'normal',
}: CarruselLogosAlianzasProps) {
	const animationDuration = {
		slow: '60s',
		normal: '40s',
		fast: '20s',
	}[speed]

	// Calculate min-width based on number of logos (each logo ~150px avg + 127px gap)
	const logoSetMinWidth = alianzaLogos.length * 150 + (alianzaLogos.length - 1) * 127

	return (
		<div
			className={cn(
				'relative h-[116px] w-full overflow-hidden bg-white',
				className,
			)}
		>
			{/* Continuous scrolling animation */}
			<div
				className={cn(
					'absolute flex h-full items-center gap-[127px]',
					autoPlay && 'animate-scroll-left',
				)}
				style={
					{
						width: 'calc(100% * 3)', // Triple width for seamless loop
						'--animation-duration': animationDuration,
					} as React.CSSProperties
				}
			>
				{/* First set of logos */}
				<div
					className="flex items-center justify-center gap-[127px]"
					style={{ minWidth: `${logoSetMinWidth}px` }}
				>
					{alianzaLogos.map((logo, index) => (
						<div
							key={`set1-${index}`}
							className="flex flex-shrink-0 items-center justify-center"
							style={{ width: `${logo.width}px`, height: `${logo.height}px` }}
						>
							<img
								src={logo.src}
								alt={logo.alt}
								className="max-h-full max-w-full object-contain"
								loading="lazy"
							/>
						</div>
					))}
				</div>

				{/* Second set of logos (for seamless loop) */}
				<div
					className="flex items-center justify-center gap-[127px]"
					style={{ minWidth: `${logoSetMinWidth}px` }}
				>
					{alianzaLogos.map((logo, index) => (
						<div
							key={`set2-${index}`}
							className="flex flex-shrink-0 items-center justify-center"
							style={{ width: `${logo.width}px`, height: `${logo.height}px` }}
						>
							<img
								src={logo.src}
								alt={logo.alt}
								className="max-h-full max-w-full object-contain"
								loading="lazy"
							/>
						</div>
					))}
				</div>

				{/* Third set of logos (for seamless loop) */}
				<div
					className="flex items-center justify-center gap-[127px]"
					style={{ minWidth: `${logoSetMinWidth}px` }}
				>
					{alianzaLogos.map((logo, index) => (
						<div
							key={`set3-${index}`}
							className="flex flex-shrink-0 items-center justify-center"
							style={{ width: `${logo.width}px`, height: `${logo.height}px` }}
						>
							<img
								src={logo.src}
								alt={logo.alt}
								className="max-h-full max-w-full object-contain"
								loading="lazy"
							/>
						</div>
					))}
				</div>
			</div>

			{/* Gradient fade effects for smooth edges */}
			<div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-20 bg-gradient-to-r from-white to-transparent" />
			<div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-20 bg-gradient-to-l from-white to-transparent" />
		</div>
	)
}

// CSS animation for seamless scrolling (to be added to global CSS)
export const carruselAnimationCSS = `
@keyframes scroll-left {
  0% { transform: translateX(0); }
  100% { transform: translateX(calc(-100% / 3)); }
}

.animate-scroll-left {
  animation: scroll-left var(--animation-duration, 40s) linear infinite;
}

.animate-scroll-left:hover {
  animation-play-state: paused;
}
`
